'use client';

import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { AssistivLogoSmallSVG } from '@/assets/branding/assistiv';
import type { Config, LogoSrc, LogoSvg, RemoteLogo } from '@/contexts/ConfigContext';
import Request from '@/services/util/request-service';

type BrandLogoProps = {
  logo?: Config['logo'] | null;
  brandName: string;
  className?: string;
  style?: CSSProperties;
  fallbackClassName?: string;
  fallbackStyle?: CSSProperties;
};

const isLogoSrc = (logo: Config['logo'] | null | undefined): logo is LogoSrc =>
  Boolean(logo && typeof logo === 'object' && 'src' in logo && typeof logo.src === 'string');

const isSvgLogoUrl = (logoUrl: string | undefined): boolean => {
  if (!logoUrl) {
    return false;
  }

  try {
    return new URL(logoUrl).pathname.toLowerCase().endsWith('.svg');
  } catch {
    return logoUrl.split('?')[0].toLowerCase().endsWith('.svg');
  }
};

const getLogoPathname = (logoUrl: string): string => {
  try {
    return new URL(logoUrl).pathname;
  } catch {
    return logoUrl.split('?')[0];
  }
};

const getVersionedLogoProxyEndpoint = (logoUrl: string): string | null => {
  const pathSegments = getLogoPathname(logoUrl).split('/').filter(Boolean);
  const brandingSegmentIndex = pathSegments.indexOf('branding');
  const subdomain = pathSegments[brandingSegmentIndex + 1];
  const logoSegment = pathSegments[brandingSegmentIndex + 2];
  const logoVersion = pathSegments[pathSegments.length - 1];

  if (brandingSegmentIndex === -1 || !subdomain || logoSegment !== 'logo') {
    return null;
  }

  const endpoint = `branding/${encodeURIComponent(subdomain)}/logo`;
  return logoVersion ? `${endpoint}/${encodeURIComponent(logoVersion)}` : endpoint;
};

const logoProxyEndpoint = (logoUrl: string, retryNonce: number): string => {
  const endpoint = getVersionedLogoProxyEndpoint(logoUrl) ?? 'branding/logo';
  const params = new URLSearchParams();
  if (retryNonce) {
    params.set('v', String(retryNonce));
  }

  const queryString = params.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
};

const fallbackLabel = 'Assistiv Logo';

export default function BrandLogo({
  logo,
  brandName,
  className,
  style,
  fallbackClassName,
  fallbackStyle,
}: BrandLogoProps): React.JSX.Element {
  const [retryNonce, setRetryNonce] = useState(0);
  const [failed, setFailed] = useState(false);
  const [remoteSvgLogoMarkup, setRemoteSvgLogoMarkup] = useState<string | null>(null);
  const isRemoteLogo = typeof logo === 'string';
  const isRemoteSvgLogo = isRemoteLogo && isSvgLogoUrl(logo as RemoteLogo);
  const proxyEndpoint = useMemo(
    () => (isRemoteLogo ? logoProxyEndpoint(logo as RemoteLogo, retryNonce) : 'branding/logo'),
    [isRemoteLogo, logo, retryNonce],
  );
  const proxyUrl = useMemo(() => Request.buildUrl(proxyEndpoint), [proxyEndpoint]);

  useEffect(() => {
    setRetryNonce(0);
    setFailed(false);
    setRemoteSvgLogoMarkup(null);
  }, [logo]);

  useEffect(() => {
    let isActive = true;
    setRemoteSvgLogoMarkup(null);

    if (!isRemoteSvgLogo || failed) {
      return () => {
        isActive = false;
      };
    }

    const loadRemoteSvgLogo = async () => {
      try {
        const response = await Request.get(proxyEndpoint);
        if (!response) {
          throw new Error('Logo SVG request failed');
        }

        const svgText = await response.text();
        if (!svgText.trimStart().startsWith('<svg')) {
          throw new Error('Logo response was not SVG markup');
        }

        const sanitizedSvg = DOMPurify.sanitize(svgText, {
          USE_PROFILES: { svg: true, svgFilters: true },
        });
        if (isActive) {
          setRemoteSvgLogoMarkup(sanitizedSvg);
        }
      } catch (error) {
        if (isActive) {
          if (retryNonce === 0) {
            setRetryNonce(Date.now());
          } else {
            setFailed(true);
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.warn('[branding] failed to inline remote SVG logo:', error);
        }
      }
    };

    void loadRemoteSvgLogo();

    return () => {
      isActive = false;
    };
  }, [failed, isRemoteSvgLogo, proxyEndpoint, retryNonce]);

  const renderFallbackLogo = (): React.JSX.Element => (
    <AssistivLogoSmallSVG
      aria-label={fallbackLabel}
      className={fallbackClassName ?? className}
      style={fallbackStyle ?? style}
    />
  );

  if (typeof logo === 'function') {
    const Logo = logo as LogoSvg;
    return <Logo aria-label={`${brandName} Logo`} className={className} style={style} />;
  }

  if (isLogoSrc(logo) && !failed) {
    return (
      <img
        src={logo.src}
        alt={`${brandName} Logo`}
        className={className}
        style={style}
        onError={() => setFailed(true)}
      />
    );
  }

  if (isRemoteLogo && !failed) {
    if (isRemoteSvgLogo) {
      if (remoteSvgLogoMarkup) {
        return (
          <span
            aria-label={`${brandName} Logo`}
            className={className}
            style={style}
            role='img'
            dangerouslySetInnerHTML={{ __html: remoteSvgLogoMarkup }}
          />
        );
      }

      return renderFallbackLogo();
    }

    return (
      <img
        src={proxyUrl}
        alt={`${brandName} Logo`}
        className={className}
        style={style}
        onError={() => {
          if (retryNonce === 0) {
            setRetryNonce(Date.now());
          } else {
            setFailed(true);
          }
        }}
      />
    );
  }

  return renderFallbackLogo();
}
